import React from "react";
import { FiDroplet, FiActivity, FiAlertTriangle, FiCheckCircle, FiWind, FiZap } from "react-icons/fi";
import { MdOutlineWaterDrop, MdRecycling } from "react-icons/md";
import { KpiCard, HealthRing, stageStatusColor } from "./stpHelpers";

const StpOverviewPanel = ({ d }) => {
  const { summary, site, processStages } = d;
  const maxWeekly = Math.max(...d.inlet.weeklyTrend.map(x => x.volume));

  return (
    <div className="stp-overview">

      {/* ── Top: Health + Site + AI ── */}
      <div className="stp-ov-top">
        <div className="stp-health-card">
          <HealthRing score={summary.healthScore} size={90} />
          <div>
            <div className="stp-health-title">STP Health Score</div>
            <div className="stp-health-name">{site.name}</div>
            <div className="stp-health-loc">{site.location}</div>
            <div className="stp-health-tech">{site.technology}</div>
            <div className="stp-health-cap">Capacity: {site.capacity.toLocaleString()} m³/day</div>
            <div className="stp-health-load">Current Load: {((site.currentLoad / site.capacity) * 100).toFixed(0)}%</div>
          </div>
        </div>

        <div className="stp-compliance-card">
          <div className="stp-cc-title">Compliance Status</div>
          <div className={`stp-compliance-badge ${summary.complianceStatus === "Compliant" ? "stp-comp-ok" : "stp-comp-fail"}`}>
            {summary.complianceStatus === "Compliant" ? "✅ Compliant" : "❌ Non-Compliant"}
          </div>
          <div className="stp-cc-authority">{d.compliance.authorityName}</div>
          <div className="stp-cc-permit">Permit: {d.compliance.permitNumber}</div>
          <div className="stp-cc-audit">Last audit: {d.compliance.lastAuditDate}</div>
          <div className="stp-cc-audit">Next audit: {d.compliance.nextAuditDate}</div>
          <div className="stp-cc-violations">Violations (30 days): <strong style={{ color: d.compliance.violations > 0 ? "#ef4444" : "#10b981" }}>{d.compliance.violations}</strong></div>
        </div>

        <div className="stp-ai-card">
          <div className="stp-ai-title">AI Insights</div>
          {summary.aiInsights.map((ins, i) => (
            <div key={i} className="stp-ai-row">
              <span className="stp-ai-dot" />
              {ins}
            </div>
          ))}
        </div>
      </div>

      {/* ── KPI Grid ── */}
      <div className="stp-kpi-grid">
        <KpiCard label="Inlet Flow"        value={summary.inletFlow.toLocaleString()}   unit="m³/day" color="#667eea" icon={FiDroplet}       sub={`${d.inlet.flowRate} m³/hr current`} />
        <KpiCard label="Treated Output"    value={summary.treatedOutput.toLocaleString()} unit="m³/day" color="#10b981" icon={MdOutlineWaterDrop} sub="Today total" />
        <KpiCard label="Reuse Percentage"  value={summary.reusePercentage}              unit="%"      color="#3b82f6" icon={MdRecycling}       sub="Treated water reused" />
        <KpiCard label="Blowers Running"   value={`${summary.blowersRunning}/${summary.blowersTotal}`} color="#f97316" icon={FiWind}    sub="Active blowers" />
        <KpiCard label="Active Alarms"     value={summary.activeAlarms}                               color="#ef4444" icon={FiAlertTriangle}  alert={summary.activeAlarms > 2 ? "Requires attention" : null} />
        <KpiCard label="Compliance"        value={summary.complianceStatus === "Compliant" ? "✓" : "✗"} color={summary.complianceStatus === "Compliant" ? "#10b981" : "#ef4444"} icon={FiCheckCircle} sub={summary.complianceStatus} />
        <KpiCard label="Total Power"       value={(d.aeration.blowers.reduce((s, b) => s + b.power, 0)).toFixed(1)} unit="kW" color="#8b5cf6" icon={FiZap} sub="Blower energy today" />
        <KpiCard label="Pumps Running"     value={d.pumps.filter(p => p.status === "Running").length} color="#06b6d4" icon={FiActivity} sub={`of ${d.pumps.length} total pumps`} />
      </div>

      {/* ── Process Stage Summary ── */}
      <div className="stp-ov-bottom">
        <div className="stp-stage-summary-card">
          <div className="stp-section-title">Process Stage Status</div>
          <div className="stp-stage-chips">
            {processStages.map(s => {
              const st = stageStatusColor(s.status);
              return (
                <div key={s.id} className="stp-stage-chip" style={{ borderColor: st.border, background: st.bg + "44" }}>
                  <div className="stp-sc-id">{s.id}</div>
                  <div className="stp-sc-name">{s.name}</div>
                  <div className="stp-sc-level">Level: {s.level != null ? `${s.level}%` : "—"}</div>
                  <span className="stp-badge" style={{ background: st.bg, color: st.color, fontSize: 10 }}>{st.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="stp-weekly-card">
          <div className="stp-section-title">Inlet Volume — Last 7 Days</div>
          <div className="stp-bar-chart">
            {d.inlet.weeklyTrend.map(day => (
              <div key={day.day} className="stp-bar-col">
                <div className="stp-bar-val">{(day.volume / 1000).toFixed(1)}k</div>
                <div className="stp-bar-wrap">
                  <div className="stp-bar-fill" style={{ height: `${(day.volume / maxWeekly) * 100}%`, background: "linear-gradient(180deg,#667eea,#764ba2)" }} />
                </div>
                <div className="stp-bar-lbl">{day.day}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StpOverviewPanel;
